import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { cardsDownloadRequest, getCardsRequest } from '../../actions/cardActions';
import { getCardOptionsReset } from 'actions/storeActions';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from '@ampol/design-system';
import { selectCardsPageContext } from '../../selectors/cardsSelectors';
import { trackCustomEvent } from 'utilities/app-insights';

import { useCardsSelection } from './hooks/useCardsSelection';
import { useCardsFilters } from './hooks/useCardsFilters';
import { useCardsActionMenu } from './hooks/useCardsActionMenu';
import { useCardsData } from './hooks/useCardsData';

import './cards.scss';
import ContentControls from '../../components/content-controls/content-controls';
import FilterControl from '../../components/filter-control/filter-control';
import Paginator from '../../components/pagination/pagination';
import CardUpdatePinFormConfirmation from '../../components/card-update-pin-form-confirmation/card-update-pin-form-confirmation';
import ActionMenu from '../../components/action-menu/action-menu';
import ActionModal from '../../components/_global/action-modal/action-modal';
import CardResetPin from '../../components/card-reset-pin/card-reset-pin';
import TransferCardsForm from 'features/cards/components/transfer-cards-form/transfer-cards-form';
import CardUsageOptionsModal from 'features/cards/modals/card-usage-options-modal/card-usage-options-modal';
import AcknowledgeModal from 'features/cards/modals/acknowledge-modal/acknowledge-modal';
import EditMultipleCardWrapper from 'features/cards/components/edit-multiple-card-wrapper/edit-multiple-card-wrapper';
import Validation from 'constants/validation-messages';
import Label from 'constants/labels';
import ActivateCard from 'features/cards/components/activate-card/activate-card';
import CardsFilter from 'features/cards/components/cards-filter/cards-filter';
import EditCardStatus from 'features/cards/components/edit-single-card-status/edit-single-card-status';
import useCardEditStatusModal from 'features/cards/components/new-card-modal/card-edit-status-modal/useCardEditStatusModal';
import { SelectStatusFormValues } from 'features/cards/components/edit-single-card-status/SelectStatusForm';
import { defualtPageLengths } from '../../constants';
import Env from 'Env';

export interface CardsProp {
  // eslint-disable-next-line no-unused-vars
  onItemSelected: (data: any) => void;
  contractNumber: string;
  modalType?: string;
  accountLevel?: string;
}

export interface CardDetails {
  cardNumber: string;
  costCentre: string;
  domesticNumber: string;
  driverName: string;
  issueDate: string;
  parent: string;
  registrationNumber: string;
  status: string;
}

const Cards = ({ modalType, onItemSelected, contractNumber, accountLevel }: CardsProp) => {
  const dispatch = useDispatch();
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;

  const {
    cardResults,
    cardResultsSize,
    isCardsLoading,
    isCardsLoaded,
    isCardsError,
    isCardNumberAdded,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    canReadWrite,
    vehicleTypes,
    updateSingleCardStatusLoaded,
    updateMultipleCardStatusLoaded,
    activateSingleCardLoaded,
    activateMultipleCardLoaded,
    createCardLoaded,
    editCardLoaded,
    editMultipleCardLoaded,
    transferSingleCardLoaded,
    transferMultipleCardLoaded,
    globalSearchLoaded,
    globalSearchResult,
    globalSearchType,
    isDataTableLoading,
  } = useSelector(selectCardsPageContext);

  // Default filter configuration
  const defaultFilterValues = {
    cfStartRange: '',
    cfEndRange: '',
    cfAccountNumber: null,
    cfStatus: 'All',
    cfIssueDateFrom: null,
    cfIssueDateTo: null,
    cfDriverName: '',
    cfVehicleRegNumber: '',
    cfDomesticNumber: '',
    cfCostCentre: '',
  };

  // Hook 1: Selection Management (~150 lines extracted)
  const selectionHook = useCardsSelection(cardResults);
  const {
    selection,
    selectAll,
    selectedCards,
    handleSelectionChange,
    triggerSelectAll,
    resetSelection,
    getNumberOfSelectedRecords,
    getSelectedCards,
    getSelectedCardAccounts,
    getSelectedCardStatuses,
    getSelectedCardPOSVerificationType,
    isAllSelectedCardsInactive,
    canSelectCard,
  } = selectionHook;

  // Hook 2: Filter Management (~200 lines extracted)
  const {
    filterValues,
    filterDisplay,
    filterApplied,
    setFilterValues,
    setFilterDisplay,
    toggleFilter: baseToggleFilter,
    submitCardFilter: baseSubmitCardFilter,
    resetFilterValues,
    sortCards,
    handlePageValue: baseHandlePageValue,
    isFilterApplied,
  } = useCardsFilters(defaultFilterValues, modalType, contractNumber, loggedInUser, assignedAccountNumber);

  // Hook 3: Action Menu Management (~300 lines extracted)
  const {
    actionMenu,
    actionMenuItems,
    offsetLeftPosition,
    handleActionMenuClick,
    closeActionMenuDialog,
    displayUpdateCardDetailsModal,
    closeUpdateCardStatus,
  } = useCardsActionMenu(
    selection,
    getSelectedCards,
    getSelectedCardStatuses,
    getSelectedCardAccounts,
    getSelectedCardPOSVerificationType,
    isAllSelectedCardsInactive,
    loggedInUser,
    assignedAccountNumber,
    setFilterDisplay // Pass the actual setter from filters hook
  );

  // STABILITY FIX: Memoize row selection handler to prevent recreation on every render
  // This callback depends on cardResults and is passed to useCardsData hook
  const handleRowSelect = useCallback(
    (rowData: any) => {
      if (modalType === 'account') {
        window.dataLayer.push({
          event: 'Card_Portal_Accounts_Cards_Select',
          selected: true,
        });
      } else {
        window.dataLayer.push({
          event: 'Card_Portal_All_Individual_Card_Select',
          button_click: true,
        });
      }
      resetSelection();

      const selectedItem = cardResults.find((r) => r.cardNumber === rowData.cardNumber);
      onItemSelected(selectedItem);
    },
    [modalType, resetSelection, cardResults, onItemSelected]
  );

  // Hook 4: Data Fetching & Lifecycle (~300 lines extracted)
  const { getCardsList, getCardsRequestPayload } = useCardsData({
    filterValues,
    setFilterValues,
    resetSelection,
    resetFilterValues,
    modalType,
    contractNumber,
    accountLevel,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    vehicleTypes,
    isCardsLoaded,
    isCardNumberAdded,
    globalSearchLoaded,
    globalSearchResult,
    globalSearchType,
    cardResults,
    transferSingleCardLoaded,
    transferMultipleCardLoaded,
    activateSingleCardLoaded,
    activateMultipleCardLoaded,
    createCardLoaded,
    editCardLoaded,
    editMultipleCardLoaded,
    updateSingleCardStatusLoaded,
    updateMultipleCardStatusLoaded,
    handleRowSelect,
    setFilterDisplay, // Pass the actual setter from filters hook
  });

  // Local state for update card status (minimal state remaining in component)
  // eslint-disable-next-line no-unused-vars
  const [updateCardStatusRequest, setUpdateCardStatusRequest] = useState({
    cardStatusSelected: '',
  });
  const [showBatchEvAcknowledgeModal, setShowBatchEvAcknowledgeModal] = useState(false);
  // Watches the setCardOptions saga completing after an EV batch reissue.
  const [evReissuePending, setEvReissuePending] = useState(false);
  const wasCardOptionsLoadingRef = useRef(false);
  const cardOptionsLoading = useSelector((state: any) => state.cardUsageOptions.isLoading);
  const cardOptionsIsError = useSelector((state: any) => state.cardUsageOptions.isError);

  useEffect(() => {
    if (evReissuePending && wasCardOptionsLoadingRef.current && !cardOptionsLoading && !cardOptionsIsError) {
      setEvReissuePending(false);
      setShowBatchEvAcknowledgeModal(true);
    }
    wasCardOptionsLoadingRef.current = cardOptionsLoading;
  }, [cardOptionsLoading, evReissuePending]);

  const tableId = useMemo(() => `table-${Date.now()}`, []);

  // Memoize selected card accounts to prevent reference instability.
  // getSelectedCardAccounts() returns a new array on every call. When cards.tsx
  // re-renders (e.g., vehicle types loading changes getVehicleTypes in the selector),
  // passing getSelectedCardAccounts() inline would give CardUsageOptionsForm a new
  // contractNumber reference, re-firing the /default effect and causing 429 errors.
  const selectedCardAccounts = useMemo(() => getSelectedCardAccounts(), [getSelectedCardAccounts]);

  // Wrapper functions to sync with action menu
  const toggleFilter = () => {
    baseToggleFilter();
    // Close all action menu dialogs when filter is toggled
    closeActionMenuDialog();
  };

  const submitCardFilter = (data: any) => {
    resetSelection();
    baseSubmitCardFilter(data);
  };

  const handlePageValue = (pageNumber: number, pageSize: number) => {
    resetSelection();
    baseHandlePageValue(pageNumber, pageSize);
  };

  // UTILITY FUNCTIONS - Component-specific helpers only
  // (All selector/filter/action menu logic moved to custom hooks)

  // DataTable column configuration
  const getDataTableColumns = () => {
    return [
      !modalType && {
        header: 'Account number',
        accessorKey: 'parent',
        id: 'Parent',
      },
      {
        header: 'Card Number',
        accessorKey: 'cardNumber',
        id: 'CardNumber',
      },
      {
        header: 'Card Status',
        accessorKey: 'status',
        id: 'Status',
      },
      {
        header: 'Vehicle Rego',
        accessorKey: 'registrationNumber',
        id: 'RegistrationNumber',
      },
      {
        header: 'Driver Name',
        accessorKey: 'driverName',
        id: 'DriverName',
      },
      {
        header: 'Domestic Number',
        accessorKey: 'domesticNumber',
        id: 'DomesticNumber',
      },
      {
        header: 'Cost Centre Code',
        accessorKey: 'costCentre',
        id: 'CostCentre',
      },
    ].filter(Boolean);
  };

  // Edit Multiple Card Component data binding
  const getSelectedCardDetails = (): CardDetails[] => {
    const cardsList = getSelectedCards();
    const cardDetailsList: CardDetails[] = [];

    for (let i = 0; i < cardsList.length; i++) {
      const cardDetail = cardResults.filter((r) => r.cardNumber === cardsList[i]);
      if (cardDetail[0]) {
        cardDetailsList.push(cardDetail[0]);
      }
    }

    return cardDetailsList;
  };

  // Download cards with GTM tracking
  const downloadCards = () => {
    const payload = getCardsRequestPayload();
    const payloadWithPagination = {
      ...payload,
      page: 0,
      pageSize: 300,
    };
    const cardsList = getSelectedCards();

    window.dataLayer.push({
      event: 'Card_Portal_All _Download_Results',
      button_click: true,
      cards_selected: cardsList && cardsList.length > 0 ? cardsList.length : 0,
    });

    dispatch(cardsDownloadRequest(payloadWithPagination));

    trackCustomEvent(
      'Click Download',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Download to download list of cards.'
    );
  };

  // Reset card selection (delegates to selection hook)
  const resetCardSelection = () => {
    resetSelection();
  };

  // Card status edit modal hook
  const cardStatusEditModal = useCardEditStatusModal(
    getSelectedCards(),
    closeActionMenuDialog,
    getSelectedCardDetails(),
    true
  );

  const onChangeCardStatusSubmitted = (values: SelectStatusFormValues) => {
    cardStatusEditModal.open(values);
    closeUpdateCardStatus();
  };

  return (
    <div className='card-content-zone'>
      <div className='card-content-zone__sticky-action-menu'>
        <FilterControl
          showFilterControl={true}
          filterToggle={toggleFilter}
          filterDisplay={filterDisplay}
          filterApplied={filterApplied}
        >
          <div className='card-filter-zone'>
            <CardsFilter
              modalType={modalType}
              resetFilterValues={() => {
                window.dataLayer.push({
                  event: 'Card_Portal_All_Clear_All',
                  button_click: true,
                });
                resetFilterValues();
              }}
              onSubmit={submitCardFilter}
              filterValues={filterValues}
            />
          </div>
        </FilterControl>
        <ContentControls
          showPageControls={true}
          showFilterControl={false}
          filterApplied={filterApplied}
          handleDownload={downloadCards}
          selectControl={!isCardsLoading && canReadWrite}
          triggerSelectAll={triggerSelectAll}
          isSelected={selectAll}
          totalRecords={cardResultsSize}
          selectedItemCount={getNumberOfSelectedRecords()}
          pageNumber={filterValues?.pageNumber}
          itemCount={cardResultsSize}
          pageLengths={defualtPageLengths}
          onPageChange={handlePageValue}
        />
        {actionMenu?.displayActionMenu && !isDataTableLoading && (
          <ActionMenu items={actionMenuItems} clickHandler={handleActionMenuClick} />
        )}
      </div>
      <div className='update-pin'>
        {actionMenu?.displayUpdatePin && (
          <ActionModal title={Label.UPDATE_PIN} onClose={closeActionMenuDialog} leftPosition={offsetLeftPosition}>
            <CardUpdatePinFormConfirmation
              headless
              closeForm={closeActionMenuDialog}
              cardNumbers={getSelectedCards()}
              confirmationMessage={`This option will set a PIN on the selected ${getNumberOfSelectedRecords() === 1 ? 'card' : 'cards'} – would you like to proceed? Please ensure your drivers have been notified of the change.`}
            />
          </ActionModal>
        )}
      </div>
      <div className='reset-pin'>
        {actionMenu?.displayResetPin && (
          <ActionModal title={Label.RESET_PIN} onClose={closeActionMenuDialog} leftPosition={offsetLeftPosition}>
            <CardResetPin headless closeForm={closeActionMenuDialog} cardsList={getSelectedCards()} />
          </ActionModal>
        )}
      </div>
      <div className='update-card-status'>
        {actionMenu?.displayUpdateCardStatus && (
          <ActionModal title={'Update Status'} onClose={closeActionMenuDialog} leftPosition={offsetLeftPosition}>
            <EditCardStatus
              headless
              currentStatus={getSelectedCardStatuses()[0]}
              onSubmit={onChangeCardStatusSubmitted}
            />
          </ActionModal>
        )}
      </div>
      <div className='transfer-card'>
        {actionMenu.displayTransferCard && (
          <ActionModal title={Label.TRANSFER_CARD} onClose={closeActionMenuDialog} leftPosition={offsetLeftPosition}>
            <TransferCardsForm cardSelection={selection} closeForm={closeActionMenuDialog} />
          </ActionModal>
        )}
      </div>
      <div className='activate-card'>
        {actionMenu.displayActivateCard && (
          <ActionModal title={Label.ACTIVATE_CARD} onClose={closeActionMenuDialog} leftPosition={offsetLeftPosition}>
            <ActivateCard onClose={closeActionMenuDialog} cardList={getSelectedCards()} />
          </ActionModal>
        )}
      </div>
      <div className='update-card-usage'>
        {isEvWay4Enabled && showBatchEvAcknowledgeModal && (
          <AcknowledgeModal
            title='Card Reissue'
            content={Label.EV_CHARGING_REISSUE_NOTIFICATION}
            onClose={() => {
              dispatch(getCardOptionsReset());
              setShowBatchEvAcknowledgeModal(false);
              closeActionMenuDialog();
            }}
          />
        )}
        {actionMenu?.displayUpdateCardUsage && (
          <CardUsageOptionsModal
            cardAccountsList={selectedCardAccounts}
            onClose={closeActionMenuDialog}
            cardsList={getSelectedCards()}
            cardsData={cardResults?.filter((c) => getSelectedCards().includes(c.cardNumber))}
            onEvReissueComplete={() => setEvReissuePending(true)}
          />
        )}
      </div>
      <div>
        {actionMenu?.displayUpdateCardDetails && (
          <EditMultipleCardWrapper
            cardStatus={updateCardStatusRequest && updateCardStatusRequest?.cardStatusSelected}
            displayUpdateCardDetailsModal={displayUpdateCardDetailsModal}
            cardDetailsList={getSelectedCardDetails()}
            userState={loggedInUser}
            grandParentAccountNumber={assignedGrandfatherClientNumber}
            getCardsRequest={() => dispatch(getCardsRequest(getCardsRequestPayload()))}
            resetCardSelection={() => resetCardSelection()}
          />
        )}
      </div>
      <div className='card-table'>
        <DataTable
          caption='Card list'
          columns={getDataTableColumns()}
          data={cardResults}
          isLoading={isDataTableLoading}
          tableVariant={'list'}
          onColumnSorted={sortCards}
          onRowClick={handleRowSelect}
          isSelectable={canReadWrite}
          onSelect={handleSelectionChange}
          noDataMessage={
            isCardsLoaded || (modalType && accountLevel !== 'C')
              ? Validation.NO_CONTENT
              : isCardsError
                ? Validation.CONTENT_ERROR
                : Validation.FILTER_CARDS
          }
          rowSelectionDisabledFn={(cr) => !canSelectCard(cr)}
          rowSelectedFn={(item) => selection && selection[item.cardNumber]?.isSelected}
          rowIdFn={(row) => tableId + '_' + row.cardNumber}
        />
      </div>
      <div className='mobile-paginator-control'>
        <Paginator
          itemCount={cardResultsSize}
          pageLengths={defualtPageLengths}
          onPageChange={handlePageValue}
          name={'cards'}
        />
      </div>
    </div>
  );
};

export default Cards;
